import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import jobApplicationService from '../../../services/jobApplicationService';
import { STAGES } from './PipelineTracker';

/**
 * MoveStageModal
 * Popup for advancing an applicant to a new pipeline stage.
 * Calls POST /job-applications/{id}/move-stage
 * The backend (JobApplicationController::moveStage) maps pipeline_stage -> status
 * so the list table and the pipeline tracker always show the same thing.
 * This same mapping is mirrored here just for display, so the popup shows
 * what status the applicant will end up with before you click Save.
 */

// Must match $stageToStatusMap in JobApplicationController.php exactly.
const STAGE_TO_STATUS_LABEL = {
  applied: 'Applied',
  resume_review: 'Screening',
  screening: 'Screening',
  shortlisted: 'Screening',
  ai_screening: 'Screening',
  hr_screening: 'Screening',
  technical_interview: 'Interview',
  manager_interview: 'Interview',
  final_hr: 'Interview',
  offer_released: 'Offer',
  offer_accepted: 'Selected',
  background_verification: 'Selected',
  joined: 'Hired'
};

const MoveStageModal = ({ applicant, interviews = [], onClose, onSuccess }) => {
  const rawStage = (applicant?.pipeline_stage || '').toLowerCase().trim();
  const normalizedStage = rawStage.replace(/[\s\-]+/g, '_');

  const currentIndex = STAGES.findIndex((s) => 
    s.key === rawStage || 
    s.key === normalizedStage || 
    s.label.toLowerCase() === rawStage ||
    (s.key === 'screening' && (rawStage === 'ai_screening' || rawStage === 'hr_screening' || normalizedStage === 'ai_screening' || normalizedStage === 'hr_screening'))
  );

  const currentStageKey = STAGES[currentIndex]?.key || normalizedStage || 'applied';

  const isFinalHrStage = ['final_hr', 'final hr', 'final_hr_round', 'final hr round', 'final_hr_discussion', 'offer_released', 'offer_accepted'].includes(rawStage) ||
                         ['final_hr', 'final hr', 'final_hr_round', 'final hr round', 'final_hr_discussion', 'offer_released', 'offer_accepted'].includes(normalizedStage) ||
                         ['final_hr', 'final hr', 'final_hr_round', 'final hr round', 'final_hr_discussion', 'offer_released', 'offer_accepted'].includes(currentStageKey);

  const isBgVerificationStage = ['background_verification', 'background verification', 'bg_verification'].includes(rawStage) ||
                                ['background_verification', 'background verification', 'bg_verification'].includes(normalizedStage) ||
                                ['background_verification', 'background verification', 'bg_verification'].includes(currentStageKey);

  const isJoinedStage = ['joined', 'joined_stage', 'onboarded'].includes(rawStage) ||
                        ['joined', 'joined_stage', 'onboarded'].includes(normalizedStage) ||
                        ['joined', 'joined_stage', 'onboarded'].includes(currentStageKey);

  const defaultNextStage = isFinalHrStage
    ? (currentStageKey === 'offer_accepted' ? 'background_verification' : 'offer_accepted')
    : isBgVerificationStage
    ? 'joined'
    : isJoinedStage
    ? 'joined'
    : (STAGES[Math.min(currentIndex + 1, STAGES.length - 1)]?.key || 'applied');

  const [newStage, setNewStage] = React.useState(defaultNextStage);
  const [remarks, setRemarks] = React.useState(applicant?.final_hr_notes || applicant?.bg_verification_notes || applicant?.joined_notes || '');
  const [interviewer, setInterviewer] = React.useState('');
  const [interviewDate, setInterviewDate] = React.useState('');
  const [offeredCtc, setOfferedCtc] = React.useState(applicant?.offered_ctc || applicant?.expected_salary || '');
  const [offeredDesignation, setOfferedDesignation] = React.useState(applicant?.offered_designation || applicant?.position || '');
  const [joiningDate, setJoiningDate] = React.useState(
    applicant?.joining_date 
      ? applicant.joining_date.split('T')[0] 
      : new Date(Date.now() + 15 * 86400000).toISOString().split('T')[0]
  );
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [sendingOfferEmail, setSendingOfferEmail] = React.useState(false);
  const [emailNotice, setEmailNotice] = React.useState(null);

  const [employmentType, setEmploymentType] = React.useState(applicant?.employment_type || 'Full Time');
  const [workLocation, setWorkLocation] = React.useState(applicant?.work_location || applicant?.location || '');
  const [noticePeriod, setNoticePeriod] = React.useState(applicant?.notice_period || '');
  const [rejectionReason, setRejectionReason] = React.useState('Salary Expectations Mismatch');
  const [customRejectionReason, setCustomRejectionReason] = React.useState('');

  const isOfferAcceptedOutcome = isFinalHrStage && (newStage === 'offer_accepted' || newStage === 'background_verification');
  const isOfferRejectedOutcome = isFinalHrStage && (newStage === 'offer_rejected' || newStage === 'rejected');

  const isBgPassedOutcome = isBgVerificationStage && (newStage === 'joined' || newStage === 'bg_passed');
  const isBgFailedOutcome = isBgVerificationStage && (newStage === 'background_verification_failed' || newStage === 'rejected' || newStage === 'bg_failed');

  const isJoinedPassedOutcome = isJoinedStage && (newStage === 'joined' || newStage === 'joined_passed');
  const isJoinedFailedOutcome = isJoinedStage && (newStage === 'not_joined' || newStage === 'joined_failed' || newStage === 'rejected');

  React.useEffect(() => {
    if (isFinalHrStage && !['offer_accepted', 'background_verification', 'offer_rejected', 'rejected'].includes(newStage)) {
      setNewStage(currentStageKey === 'offer_accepted' ? 'background_verification' : 'offer_accepted');
    } else if (isBgVerificationStage && !['joined', 'background_verification_failed', 'rejected'].includes(newStage)) {
      setNewStage('joined');
    } else if (isJoinedStage && !['joined', 'not_joined', 'rejected'].includes(newStage)) {
      setNewStage('joined');
    }
  }, [isFinalHrStage, isBgVerificationStage, isJoinedStage, currentStageKey]);

  const needsInterviewFields = ['technical_interview', 'manager_interview'].includes(newStage);
  const isFinalHrOrOffer = ['final_hr', 'offer_released', 'offer_accepted', 'background_verification'].includes(newStage);

  const getResultingStatusLabel = (stage) => {
    if (stage === 'offer_accepted' || stage === 'background_verification') return 'Selected / Offer Accepted';
    if (stage === 'joined' && isBgVerificationStage) return 'Hired (Verification Passed)';
    if (stage === 'background_verification_failed') return 'Rejected (Verification Failed)';
    if (stage === 'joined' && isJoinedStage) return 'Hired (Employee Joined)';
    if (stage === 'not_joined') return 'Rejected (Not Joined / No Show)';
    if (stage === 'offer_rejected' || stage === 'rejected') return 'Offer Rejected';
    return STAGE_TO_STATUS_LABEL[stage] || 'Applied';
  };
  const resultingStatus = getResultingStatusLabel(newStage);

  const modalContainerRef = React.useRef(null);

  const triggerError = (errMsg) => {
    setError(errMsg);
    if (modalContainerRef.current) {
      modalContainerRef.current.scrollTop = 0;
    }
  };

  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError(null);
    setEmailNotice(null);

    // Validation Guard for Offer Accepted outcome
    if (isOfferAcceptedOutcome) {
      const ctcVal = offeredCtc || applicant?.offered_ctc || applicant?.expected_salary;
      if (!ctcVal || Number(ctcVal) <= 0) {
        triggerError('Offered Annual CTC is required. Please enter a valid agreed annual CTC amount.');
        return;
      }
      if (!joiningDate) {
        triggerError('Expected Joining Date is required.');
        return;
      }
      if (!offeredDesignation) {
        triggerError('Offered Designation is required.');
        return;
      }
    }

    // Validation Guard for Offer Rejected outcome
    if (isOfferRejectedOutcome) {
      const finalReason = rejectionReason === 'Other' ? customRejectionReason : rejectionReason;
      if (!finalReason || !finalReason.trim()) {
        triggerError('Reason for Rejection is required.');
        return;
      }
    }

    // Validation Guard for Background Verification Passed outcome
    if (isBgPassedOutcome) {
      if (!joiningDate) {
        triggerError('Scheduled Joining Date is required to pass Background Verification.');
        return;
      }
    }

    setSaving(true);

    try {
      const finalReason = rejectionReason === 'Other' ? customRejectionReason : rejectionReason;
      const payload = {
        new_stage: newStage,
        remarks: remarks || null,
        interviewer: needsInterviewFields ? (interviewer || null) : null,
        interview_date: needsInterviewFields ? (interviewDate || null) : null,
        offered_ctc: isOfferAcceptedOutcome ? Number(offeredCtc || applicant?.offered_ctc || applicant?.expected_salary || 0) : null,
        offered_designation: isOfferAcceptedOutcome ? (offeredDesignation || applicant?.offered_designation || applicant?.position || '') : null,
        joining_date: (isOfferAcceptedOutcome || isBgPassedOutcome || isJoinedPassedOutcome) ? (joiningDate || applicant?.joining_date || null) : null,
        employment_type: isOfferAcceptedOutcome ? employmentType : null,
        work_location: isOfferAcceptedOutcome ? (workLocation || applicant?.work_location || applicant?.location || '') : null,
        notice_period: isOfferAcceptedOutcome ? (noticePeriod || applicant?.notice_period || '') : null,
        rejection_reason: isOfferRejectedOutcome ? finalReason : (isBgFailedOutcome ? 'Failed Background Check' : (isJoinedFailedOutcome ? 'Candidate No Show / Declined to Join' : null)),
        final_hr_notes: remarks || null,
        final_hr_status: isOfferAcceptedOutcome ? 'cleared' : (isOfferRejectedOutcome ? 'rejected' : null),
        bg_verification_status: isBgPassedOutcome ? 'passed' : (isBgFailedOutcome ? 'failed' : null),
        bg_verification_notes: isBgVerificationStage ? remarks : null,
        joined_status: isJoinedPassedOutcome ? 'joined' : (isJoinedFailedOutcome ? 'not_joined' : null),
        joined_notes: isJoinedStage ? remarks : null,
      };

      const result = await jobApplicationService.moveStage(applicant.id, payload);

      if (result.success) {
        if (typeof onSuccess === 'function') {
          onSuccess(result.data || result);
        }
        if (typeof onClose === 'function') {
          onClose();
        }
      } else {
        triggerError(result.error || 'Failed to move applicant stage');
      }
    } catch (err) {
      triggerError(err.message || 'An error occurred while moving stage');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div ref={modalContainerRef} className="bg-background border border-border rounded-xl max-w-lg w-full p-6 shadow-2xl space-y-4 animate-fade-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-border pb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold">
              <Icon name="ArrowRight" size={18} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">
                {isFinalHrStage ? 'Final HR & Offer Decision' : isBgVerificationStage ? 'Background Verification Decision' : isJoinedStage ? 'Joined / Onboarding Decision' : 'Move Pipeline Stage'}
              </h2>
              <p className="text-xs text-muted-foreground">
                Candidate: <strong className="text-foreground">{applicant?.applicant_name || 'Applicant'}</strong> ({applicant?.position})
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground rounded-lg p-1 transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-700 dark:text-red-300 text-xs flex items-start gap-2">
            <Icon name="AlertTriangle" size={16} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {emailNotice && (
          <div className={`p-3 rounded-lg border flex items-center justify-between gap-2 text-sm mb-4 ${
            emailNotice.type === 'success'
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300'
              : 'bg-red-500/10 border-red-500/30 text-red-700 dark:text-red-300'
          }`}>
            <div className="flex items-center gap-2">
              <Icon name={emailNotice.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} />
              <span>{emailNotice.message}</span>
            </div>
            <button type="button" onClick={() => setEmailNotice(null)} className="text-muted-foreground hover:text-foreground">
              <Icon name="X" size={14} />
            </button>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {isFinalHrStage || isBgVerificationStage || isJoinedStage ? 'Select Decision Outcome' : 'Move to Stage'}
            </label>
            {isFinalHrStage ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
                <button
                  type="button"
                  onClick={() => setNewStage(currentStageKey === 'offer_accepted' ? 'background_verification' : 'offer_accepted')}
                  className={`p-3.5 rounded-xl border-2 text-left transition-all flex flex-col justify-between gap-2 cursor-pointer ${
                    isOfferAcceptedOutcome
                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-900 dark:text-emerald-200 ring-2 ring-emerald-500/20 shadow-md'
                      : 'border-border bg-background hover:border-emerald-500/40 text-muted-foreground'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                      <Icon name="CheckCircle2" size={18} />
                      Offer Accepted
                    </span>
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                      isOfferAcceptedOutcome
                        ? 'border-emerald-500 bg-emerald-500 text-white'
                        : 'border-border'
                    }`}>
                      {isOfferAcceptedOutcome && <Icon name="Check" size={10} />}
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-tight">
                    {currentStageKey === 'offer_accepted'
                      ? 'Candidate accepted offer. Advance pipeline to Background Verification.'
                      : 'Mark offer accepted and record agreed offer terms.'}
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setNewStage('offer_rejected')}
                  className={`p-3.5 rounded-xl border-2 text-left transition-all flex flex-col justify-between gap-2 cursor-pointer ${
                    isOfferRejectedOutcome
                      ? 'border-red-500 bg-red-500/10 text-red-900 dark:text-red-200 ring-2 ring-red-500/20 shadow-md'
                      : 'border-border bg-background hover:border-red-500/40 text-muted-foreground'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm flex items-center gap-1.5 text-red-600 dark:text-red-400">
                      <Icon name="XCircle" size={18} />
                      Offer Rejected
                    </span>
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                      isOfferRejectedOutcome
                        ? 'border-red-500 bg-red-500 text-white'
                        : 'border-border'
                    }`}>
                      {isOfferRejectedOutcome && <Icon name="Check" size={10} />}
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-tight">
                    Candidate declined offer. Mark offer stage rejected in stepper.
                  </p>
                </button>
              </div>
            ) : isBgVerificationStage ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
                <button
                  type="button"
                  onClick={() => setNewStage('joined')}
                  className={`p-3.5 rounded-xl border-2 text-left transition-all flex flex-col justify-between gap-2 cursor-pointer ${
                    isBgPassedOutcome
                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-900 dark:text-emerald-200 ring-2 ring-emerald-500/20 shadow-md'
                      : 'border-border bg-background hover:border-emerald-500/40 text-muted-foreground'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                      <Icon name="CheckCircle2" size={18} />
                      Verification Passed
                    </span>
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                      isBgPassedOutcome
                        ? 'border-emerald-500 bg-emerald-500 text-white'
                        : 'border-border'
                    }`}>
                      {isBgPassedOutcome && <Icon name="Check" size={10} />}
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-tight">
                    Background & document check cleared. Advance pipeline directly to Joined.
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setNewStage('background_verification_failed')}
                  className={`p-3.5 rounded-xl border-2 text-left transition-all flex flex-col justify-between gap-2 cursor-pointer ${
                    isBgFailedOutcome
                      ? 'border-red-500 bg-red-500/10 text-red-900 dark:text-red-200 ring-2 ring-red-500/20 shadow-md'
                      : 'border-border bg-background hover:border-red-500/40 text-muted-foreground'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm flex items-center gap-1.5 text-red-600 dark:text-red-400">
                      <Icon name="XCircle" size={18} />
                      Verification Failed / Rejected
                    </span>
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                      isBgFailedOutcome
                        ? 'border-red-500 bg-red-500 text-white'
                        : 'border-border'
                    }`}>
                      {isBgFailedOutcome && <Icon name="Check" size={10} />}
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-tight">
                    Background check failed or flagged discrepancies. Mark stage failed and stop advancement.
                  </p>
                </button>
              </div>
            ) : isJoinedStage ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
                <button
                  type="button"
                  onClick={() => setNewStage('joined')}
                  className={`p-3.5 rounded-xl border-2 text-left transition-all flex flex-col justify-between gap-2 cursor-pointer ${
                    isJoinedPassedOutcome
                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-900 dark:text-emerald-200 ring-2 ring-emerald-500/20 shadow-md'
                      : 'border-border bg-background hover:border-emerald-500/40 text-muted-foreground'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                      <Icon name="CheckCircle2" size={18} />
                      Joined
                    </span>
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                      isJoinedPassedOutcome
                        ? 'border-emerald-500 bg-emerald-500 text-white'
                        : 'border-border'
                    }`}>
                      {isJoinedPassedOutcome && <Icon name="Check" size={10} />}
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-tight">
                    Candidate successfully joined and onboarded as an employee. Mark final stage completed.
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setNewStage('not_joined')}
                  className={`p-3.5 rounded-xl border-2 text-left transition-all flex flex-col justify-between gap-2 cursor-pointer ${
                    isJoinedFailedOutcome
                      ? 'border-red-500 bg-red-500/10 text-red-900 dark:text-red-200 ring-2 ring-red-500/20 shadow-md'
                      : 'border-border bg-background hover:border-red-500/40 text-muted-foreground'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm flex items-center gap-1.5 text-red-600 dark:text-red-400">
                      <Icon name="XCircle" size={18} />
                      Not Joined
                    </span>
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                      isJoinedFailedOutcome
                        ? 'border-red-500 bg-red-500 text-white'
                        : 'border-border'
                    }`}>
                      {isJoinedFailedOutcome && <Icon name="Check" size={10} />}
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-tight">
                    Candidate did not join or was a no-show on joining date. Mark stage failed.
                  </p>
                </button>
              </div>
            ) : (
              <select
                value={newStage}
                onChange={(e) => setNewStage(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
              >
                {STAGES.map((stage) => (
                  <option key={stage.key} value={stage.key}>
                    {stage.label}
                  </option>
                ))}
              </select>
            )}
            <p className="text-xs text-muted-foreground mt-1.5">
              This will set the applicant's status to{' '}
              <span className="font-semibold text-foreground">{resultingStatus}</span> in the list table.
            </p>
          </div>

          {/* DECISION: OFFER ACCEPTED FORM */}
          {isOfferAcceptedOutcome && (
            <>
              <div className="border border-emerald-500/40 bg-emerald-50/20 dark:bg-emerald-950/20 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2 font-semibold text-emerald-800 dark:text-emerald-200 text-sm">
                  <Icon name="FileCheck" size={18} className="text-emerald-600 dark:text-emerald-400" />
                  <span>Agreed Offer Details</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">
                      Offered Annual CTC (₹) *
                    </label>
                    <input
                      type="number"
                      value={offeredCtc}
                      onChange={(e) => setOfferedCtc(e.target.value)}
                      placeholder="e.g. 850000"
                      className="w-full px-3 py-1.5 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">
                      Expected Joining Date *
                    </label>
                    <input
                      type="date"
                      value={joiningDate}
                      onChange={(e) => setJoiningDate(e.target.value)}
                      className="w-full px-3 py-1.5 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">
                    Offered Designation / Title *
                  </label>
                  <input
                    type="text"
                    value={offeredDesignation}
                    onChange={(e) => setOfferedDesignation(e.target.value)}
                    placeholder="e.g. Senior Software Engineer"
                    className="w-full px-3 py-1.5 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">
                      Work Location
                    </label>
                    <input
                      type="text"
                      value={workLocation}
                      onChange={(e) => setWorkLocation(e.target.value)}
                      placeholder="e.g. Bangalore, India / Hybrid"
                      className="w-full px-3 py-1.5 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">
                      Notice Period / Availability
                    </label>
                    <input
                      type="text"
                      value={noticePeriod}
                      onChange={(e) => setNoticePeriod(e.target.value)}
                      placeholder="e.g. 15 Days / Immediate"
                      className="w-full px-3 py-1.5 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">
                    Employment Type
                  </label>
                  <select
                    value={employmentType}
                    onChange={(e) => setEmploymentType(e.target.value)}
                    className="w-full px-3 py-1.5 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="Full Time">Full Time</option>
                    <option value="Part Time">Part Time</option>
                    <option value="Contract">Contract</option>
                    <option value="Intern">Intern</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground mb-1">
                  Remarks / Additional Notes (Optional)
                </label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  rows={3}
                  placeholder="Notes about offer acceptance..."
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div className="border border-emerald-500/30 bg-emerald-500/10 text-emerald-900 dark:text-emerald-200 p-3 rounded-lg flex items-start gap-2.5 text-xs">
                <Icon name="CheckCircle2" size={16} className="text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="font-semibold block text-emerald-950 dark:text-emerald-100">Offer Acceptance Summary</strong>
                  <span>Offer terms recorded. Candidate stage auto-advances to Background Verification.</span>
                </div>
              </div>
            </>
          )}

          {/* DECISION: OFFER REJECTED FORM */}
          {isOfferRejectedOutcome && (
            <>
              <div className="border border-red-500/40 bg-red-50/20 dark:bg-red-950/20 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2 font-semibold text-red-800 dark:text-red-200 text-sm">
                  <Icon name="FileX" size={18} className="text-red-600 dark:text-red-400" />
                  <span>Rejection Details</span>
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">
                    Reason for Rejection *
                  </label>
                  <select
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring mb-2"
                  >
                    <option value="Salary Expectations Mismatch">Salary Expectations Mismatch</option>
                    <option value="Better Offer Elsewhere">Better Offer Elsewhere</option>
                    <option value="Failed Background Check">Failed Background Check</option>
                    <option value="Position No Longer Available">Position No Longer Available</option>
                    <option value="Candidate Declined Offer">Candidate Declined Offer</option>
                    <option value="Other">Other (Custom Reason)</option>
                  </select>

                  {rejectionReason === 'Other' && (
                    <input
                      type="text"
                      value={customRejectionReason}
                      onChange={(e) => setCustomRejectionReason(e.target.value)}
                      placeholder="Enter custom rejection reason..."
                      className="w-full px-3 py-1.5 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground mb-1">
                  Remarks / Rejection Notes (Optional)
                </label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  rows={3}
                  placeholder="Additional notes about rejection..."
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div className="border border-red-500/30 bg-red-500/10 text-red-900 dark:text-red-200 p-3 rounded-lg space-y-1.5 text-xs">
                <div className="flex items-center gap-1.5 font-semibold text-red-950 dark:text-red-100">
                  <Icon name="XCircle" size={16} className="text-red-600 dark:text-red-400" />
                  <span>Offer Rejection Summary</span>
                </div>
                <ul className="space-y-1 text-red-800 dark:text-red-300 pl-5 list-disc text-[11px]">
                  <li>Offer marked declined/rejected in pipeline stepper</li>
                  <li>Application status updated to Offer Rejected</li>
                  <li>Pipeline advancement stopped (will not advance to Background Verification)</li>
                </ul>
              </div>
            </>
          )}

              {needsInterviewFields && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Interviewer</label>
                    <input
                      type="text"
                      value={interviewer}
                      onChange={(e) => setInterviewer(e.target.value)}
                      placeholder="Interviewer name"
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Interview Date</label>
                    <input
                      type="datetime-local"
                      value={interviewDate}
                      onChange={(e) => setInterviewDate(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                </div>
              )}

              {!isFinalHrStage && !isBgVerificationStage && !isJoinedStage && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Remarks</label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    rows={3}
                    placeholder="Notes about this stage change..."
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                  />
                </div>
              )}

              {(isBgVerificationStage || isJoinedStage) && (
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">
                    {isJoinedStage ? 'Joining / Onboarding Remarks (Optional)' : 'Verification Remarks / Notes (Optional)'}
                  </label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    rows={3}
                    placeholder={isJoinedStage ? "Enter onboarding notes, employee ID reference..." : "Enter background check agency notes, document verification details..."}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              )}

          {/* DECISION: BACKGROUND VERIFICATION PASSED FORM */}
          {isBgPassedOutcome && (
            <div className="border border-emerald-500/40 bg-emerald-50/20 dark:bg-emerald-950/20 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2 font-semibold text-emerald-800 dark:text-emerald-200 text-sm">
                <Icon name="Calendar" size={18} className="text-emerald-600 dark:text-emerald-400" />
                <span>Onboarding & Scheduled Joining Date</span>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">
                  Scheduled Joining Date *
                </label>
                <input
                  type="date"
                  value={joiningDate}
                  onChange={(e) => setJoiningDate(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring font-medium"
                  required
                />
                <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
                  <Icon name="Mail" size={12} className="text-emerald-600 dark:text-emerald-400" />
                  <span>Candidate will automatically receive an onboarding email containing this joining date.</span>
                </p>
              </div>
            </div>
          )}

          {/* BACKGROUND VERIFICATION INFORMATIONAL HELP CARDS */}
          {isBgVerificationStage && (
            <div className="mt-4 pt-3 border-t border-border">
              {isBgPassedOutcome ? (
                <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-900 dark:text-emerald-200 p-3 rounded-xl space-y-1.5 text-xs">
                  <div className="flex items-center gap-1.5 font-semibold text-emerald-950 dark:text-emerald-100">
                    <Icon name="CheckCircle" size={16} className="text-emerald-600 dark:text-emerald-400" />
                    <span>What happens when Verification Passes?</span>
                  </div>
                  <ul className="space-y-1 text-emerald-800 dark:text-emerald-300 pl-5 list-disc text-[11px]">
                    <li>Background & document verification marked passed</li>
                    <li>Pipeline moves forward directly to 'Joined' stage</li>
                    <li>Stage circle turns green in hiring pipeline tracker</li>
                  </ul>
                </div>
              ) : (
                <div className="bg-red-500/10 border border-red-500/20 text-red-900 dark:text-red-200 p-3 rounded-xl space-y-1.5 text-xs">
                  <div className="flex items-center gap-1.5 font-semibold text-red-950 dark:text-red-100">
                    <Icon name="XCircle" size={16} className="text-red-600 dark:text-red-400" />
                    <span>What happens when Verification Fails?</span>
                  </div>
                  <ul className="space-y-1 text-red-800 dark:text-red-300 pl-5 list-disc text-[11px]">
                    <li>Background verification marked failed / rejected</li>
                    <li>Stage circle turns red in hiring pipeline tracker</li>
                    <li>Pipeline advancement stops (candidate remains flagged at Background Verification)</li>
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* JOINED INFORMATIONAL HELP CARDS */}
          {isJoinedStage && (
            <div className="mt-4 pt-3 border-t border-border">
              {isJoinedPassedOutcome ? (
                <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-900 dark:text-emerald-200 p-3 rounded-xl space-y-1.5 text-xs">
                  <div className="flex items-center gap-1.5 font-semibold text-emerald-950 dark:text-emerald-100">
                    <Icon name="CheckCircle" size={16} className="text-emerald-600 dark:text-emerald-400" />
                    <span>What happens when Joined is Confirmed?</span>
                  </div>
                  <ul className="space-y-1 text-emerald-800 dark:text-emerald-300 pl-5 list-disc text-[11px]">
                    <li>Candidate marked successfully joined and onboarded</li>
                    <li>Final stage circle turns green in hiring pipeline tracker</li>
                    <li>Application status updated to Hired in candidate list</li>
                  </ul>
                </div>
              ) : (
                <div className="bg-red-500/10 border border-red-500/20 text-red-900 dark:text-red-200 p-3 rounded-xl space-y-1.5 text-xs">
                  <div className="flex items-center gap-1.5 font-semibold text-red-950 dark:text-red-100">
                    <Icon name="XCircle" size={16} className="text-red-600 dark:text-red-400" />
                    <span>What happens when Not Joined is Selected?</span>
                  </div>
                  <ul className="space-y-1 text-red-800 dark:text-red-300 pl-5 list-disc text-[11px]">
                    <li>Candidate marked as no-show or declined to join</li>
                    <li>Final stage circle turns red in hiring pipeline tracker</li>
                    <li>Application status updated to Rejected</li>
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* FINAL HR INFORMATIONAL HELP CARDS */}
          {isFinalHrStage && (
            <div className="mt-4 pt-3 border-t border-border">
              {newStage === 'offer_accepted' ? (
                <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-900 dark:text-emerald-200 p-3 rounded-xl space-y-1.5 text-xs">
                  <div className="flex items-center gap-1.5 font-semibold text-emerald-950 dark:text-emerald-100">
                    <Icon name="CheckCircle" size={16} className="text-emerald-600 dark:text-emerald-400" />
                    <span>What happens when Offer is Accepted?</span>
                  </div>
                  <ul className="space-y-1 text-emerald-800 dark:text-emerald-300 pl-5 list-disc text-[11px]">
                    <li>Candidate receives offer letter with offer details</li>
                    <li>Pipeline moves to 'Offer Accepted' stage</li>
                    <li>Joining date is set and onboarding process begins</li>
                    <li>Candidate status updated in the system</li>
                  </ul>
                </div>
              ) : (
                <div className="bg-red-500/10 border border-red-500/20 text-red-900 dark:text-red-200 p-3 rounded-xl space-y-1.5 text-xs">
                  <div className="flex items-center gap-1.5 font-semibold text-red-950 dark:text-red-100">
                    <Icon name="XCircle" size={16} className="text-red-600 dark:text-red-400" />
                    <span>What happens when Offer is Rejected?</span>
                  </div>
                  <ul className="space-y-1 text-red-800 dark:text-red-300 pl-5 list-disc text-[11px]">
                    <li>Candidate receives rejection notification</li>
                    <li>Pipeline moves to 'Offer Rejected' stage</li>
                    <li>Candidate can be added to talent pool for future</li>
                    <li>Application status updated in the system</li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-700 dark:text-red-300 text-xs flex items-start gap-2 mt-2">
            <Icon name="AlertTriangle" size={16} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 mt-6">
          <Button type="button" variant="outline" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className={(isFinalHrStage || isBgVerificationStage || isJoinedStage) ? 'bg-indigo-950 hover:bg-black text-white font-semibold shadow-md px-5' : ''}
          >
            {saving
              ? 'Saving...'
              : isFinalHrStage
              ? (newStage === 'offer_accepted' ? 'Save & Move to Offer Accepted' : 'Save & Move to Offer Rejected')
              : isBgVerificationStage
              ? (isBgPassedOutcome ? 'Save & Move to Joined' : 'Save & Mark Verification Failed')
              : isJoinedStage
              ? (isJoinedPassedOutcome ? 'Save & Mark Joined' : 'Save & Mark Not Joined')
              : 'Save & Move Stage'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MoveStageModal;